import classNames from 'classnames';
import { get, map } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { Container } from 'components/Grid';
import { SecondaryHeading } from 'components/Headings';
import HomepageV2Carousel from 'components/HomepageV2Carousel';
import Paragraph from 'components/Paragraph';
import { MODULE_TYPES } from 'constants/homepage';
import { medium, largeUp } from 'constants/mediaQueries';
import CardCarouselModel from 'models/CardCarousel';
import Theme from 'styles/theme';

import CarouselCard from './CarouselCard';
import { CARD_TYPES } from './constants';
import InfoCard from './InfoCard';
import VideoCarouselCard from './VideoCarouselCard';

class CardCarousel extends React.Component {
  handleCarouselCardClick = (carouselCard, index) => {
    const { data, homepagePosition } = this.props;
    const internalName = get(data, ['_data', 'internalName']);

    const { image, linkUrl, heading } = carouselCard;
    const imageTitle = get(image, 'title');

    this.props.trackModuleEvent({
      moduleType: MODULE_TYPES.CARD_CAROUSEL,
      moduleName: internalName,
      modulePosition: homepagePosition,
      imageName: imageTitle,
      linkCTAName: heading,
      linkDestination: linkUrl,
      carouselCardPosition: index + 1,
      carouselCardName: heading,
    });
  };

  handleVideoCardClick = (carouselCard, index) => {
    const { data, homepagePosition, openVideoModal } = this.props;
    const internalName = get(data, ['_data', 'internalName']);

    const { image, videoUrl, heading } = carouselCard;
    const videoTitle = get(image, 'title');

    openVideoModal(videoUrl);
    this.trackVideoModalOpen(videoUrl);

    this.props.trackModuleEvent({
      moduleType: MODULE_TYPES.VIDEO_CAROUSEL,
      moduleName: internalName,
      modulePosition: homepagePosition,
      imageName: videoTitle,
      linkCTAName: heading,
      linkDestination: videoUrl,
      carouselCardPosition: index + 1,
      carouselCardName: heading,
    });
  };

  render() {
    const { data } = this.props;
    const { isInverted } = data;

    const showHeader = data.heading || data.subheading;

    return (
      <div
        className={classNames('CardCarousel', {
          'CardCarousel--inverted': isInverted,
        })}
      >
        {showHeader && (
          <Container>
            <div
              className={classNames('CardCarousel__heading-section', {
                'CardCarousel__heading-section--centered': data.isHeadingCentered,
              })}
            >
              {data.heading && (
                <SecondaryHeading className="CardCarousel__heading">
                  {data.heading}
                </SecondaryHeading>
              )}
              {data.subheading && (
                <Paragraph className="CardCarousel__heading-section-paragraph">
                  {data.subheading}
                </Paragraph>
              )}
            </div>
          </Container>
        )}
        <HomepageV2Carousel>
          {map(
            data.carouselCards,
            (
              { heading, subheading, image, linkUrl, videoUrl, type, cardLabel, subText },
              index
            ) => {
              switch (type) {
                case CARD_TYPES.CAROUSEL_CARD:
                  return (
                    <CarouselCard
                      key={index}
                      image={image}
                      heading={heading}
                      subheading={subheading}
                      linkUrl={linkUrl}
                      onClick={() => {
                        this.handleCarouselCardClick(
                          get(data, ['_data', 'carouselCards', index]),
                          index
                        );
                      }}
                    />
                  );
                case CARD_TYPES.VIDEO_CAROUSEL_CARD:
                  return (
                    <VideoCarouselCard
                      key={index}
                      image={image}
                      heading={heading}
                      subheading={subheading}
                      videoUrl={videoUrl}
                      onClick={() => {
                        this.handleVideoCardClick(
                          get(data.carouselCards, [index, 'heading']),
                          index,
                          linkUrl
                        );
                      }}
                    />
                  );
                case CARD_TYPES.INFO_CAROUSEL_CARD:
                  return (
                    <InfoCard
                      key={index}
                      image={image}
                      heading={heading}
                      subText={subText}
                      cardLabel={cardLabel}
                      isInverted={isInverted}
                    />
                  );
                default:
                  return null;
              }
            }
          )}
        </HomepageV2Carousel>
        <style jsx>{`
          .CardCarousel__heading-section {
            margin-bottom: ${Theme.get('spacing') * 2}px;
          }

          .CardCarousel--inverted {
            background-color: ${Theme.get('colors-black')};
          }

          .CardCarousel__heading-section--centered {
            text-align: center;
          }

          @media ${medium} {
            .CardCarousel__heading-section {
              padding: 0 ${Theme.get('spacing') * 2}px;
            }
          }

          @media ${largeUp} {
            .CardCarousel__heading-section {
              max-width: 60%;
              padding: 0 ${Theme.get('spacing') * 8}px;
            }

            .CardCarousel__heading-section--centered {
              margin: auto;
            }
          }
        `}</style>
        <style jsx global>
          {`
            .CardCarousel__heading-section-paragraph.Paragraph {
              margin-top: ${Theme.get('spacing') * 3}px;
            }

            .CardCarousel--inverted .CardCarousel__heading-section-paragraph.Paragraph,
            .CardCarousel--inverted .CardCarousel__heading.typography--secondary-heading,
            .CardCarousel--inverted .typography--quaternary-heading {
              color: ${Theme.get('colors-white')};
            }
          `}
        </style>
      </div>
    );
  }
}

CardCarousel.propTypes = {
  data: PropTypes.instanceOf(CardCarouselModel).isRequired,
  openVideoModal: PropTypes.func,
  trackModuleEvent: PropTypes.func,
  homepagePosition: PropTypes.number,
};

CardCarousel.defaultProps = {
  openVideoModal: () => {},
  trackModuleEvent: () => {},
  homepagePosition: null,
};

export default CardCarousel;
